
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Gift, Mail, Send } from 'lucide-react';
import { COMPANY_INFO, EXTERNAL_LINKS } from '../../constants';
import { useLanguage } from '../../contexts/LanguageContext';

interface FreeTrialButtonProps {
    serviceName: string;
    className?: string;
    forceNoMargin?: boolean;
    forceDirection?: 'down' | 'lateral';
    buttonText?: string; // Custom button text
    buttonIcon?: React.ReactNode; // Custom icon
    buttonClassName?: string; // Custom button styling
}

export const FreeTrialButton: React.FC<FreeTrialButtonProps> = ({
    serviceName,
    className = '',
    forceNoMargin = false,
    forceDirection,
    buttonText,
    buttonIcon,
    buttonClassName
}) => {
    const { t } = useLanguage();
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Determine if this is a trial request or general contact
    // Trial: no custom text OR text contains trial keywords
    // Contact: custom text WITHOUT trial keywords
    const hasTrialKeywords = buttonText && (
        buttonText.toLowerCase().includes('prova') ||
        buttonText.toLowerCase().includes('trial') ||
        buttonText.toLowerCase().includes('mese')
    );
    const isTrial = !buttonText || hasTrialKeywords;

    const defaultMessage = isTrial
        ? `Ciao, vorrei richiedere la prova di 1 mese per ${serviceName}`
        : `Ciao, vorrei maggiori informazioni su ${serviceName}`;

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        telegram: '',
        message: defaultMessage
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const subject = isTrial ? `Richiesta Prova - ${serviceName}` : `Richiesta Informazioni - ${serviceName}`;
        const body = `Nome: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0ATelegram: ${formData.telegram}%0D%0A%0D%0AMessaggio:%0D%0A${formData.message}`;

        window.location.href = `mailto:${COMPANY_INFO.email}?subject=${encodeURIComponent(subject)}&body=${body}`;

        setTimeout(() => setIsModalOpen(false), 1000);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // Default button styling for trial
    const defaultButtonClass = `inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 border border-green-500/30 rounded-xl text-white text-sm font-bold transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-green-500/30 ${isTrial ? 'animate-pulse' : ''}`;

    const finalButtonClass = buttonClassName || defaultButtonClass;
    const finalButtonText = buttonText || t('common.freeTrial');
    const finalButtonIcon = buttonIcon || <Gift size={18} />;

    return (
        <>
            <button
                onClick={() => setIsModalOpen(true)}
                className={`${finalButtonClass} ${className}`}
            >
                {finalButtonIcon}
                {finalButtonText}
            </button>

            {isModalOpen && createPortal(
                <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <div
                            className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-[#151E32] border border-gray-700 text-left align-middle shadow-xl transition-all animate-in zoom-in-95 duration-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="bg-[#0B1120] p-4 border-b border-gray-800 flex justify-between items-center">
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                    {isTrial ? <Gift className="text-green-500" size={20} /> : <Mail className="text-blue-500" size={20} />}
                                    {isTrial ? 'Richiedi Prova' : 'Richiedi Informazioni'}
                                </h3>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-gray-400 hover:text-white transition-colors"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="p-4">
                                <p className="text-gray-400 text-sm mb-6">
                                    {isTrial
                                        ? <>Compila il modulo per ricevere la licenza trial di 1 mese per <span className="text-green-400 font-bold">{serviceName}</span>.</>
                                        : <>Compila il modulo per ricevere maggiori informazioni su <span className="text-blue-400 font-bold">{serviceName}</span>.</>
                                    }
                                </p>

                                <form onSubmit={handleSubmit} className="space-y-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Nome Completo</label>
                                        <input
                                            type="text"
                                            name="name"
                                            required
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full bg-[#0B1120] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-green-500 focus:outline-none transition-colors"
                                            placeholder="Mario Rossi"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            required
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full bg-[#0B1120] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-green-500 focus:outline-none transition-colors"
                                            placeholder="mario@example.com"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Telegram (Opzionale)</label>
                                        <input
                                            type="text"
                                            name="telegram"
                                            value={formData.telegram}
                                            onChange={handleChange}
                                            className="w-full bg-[#0B1120] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-green-500 focus:outline-none transition-colors"
                                            placeholder="@username"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Messaggio</label>
                                        <textarea
                                            name="message"
                                            rows={2}
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="w-full bg-[#0B1120] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-green-500 focus:outline-none transition-colors resize-none"
                                        />
                                    </div>

                                    <div className="pt-1">
                                        <button
                                            type="submit"
                                            className={`w-full ${isTrial ? 'bg-green-600 hover:bg-green-500' : 'bg-blue-600 hover:bg-blue-500'} text-white font-bold py-2.5 rounded-lg transition-all shadow-lg ${isTrial ? 'shadow-green-600/20' : 'shadow-blue-600/20'} flex items-center justify-center gap-2 text-sm`}
                                        >
                                            <Send size={16} />
                                            Invia Richiesta
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                , document.body)}

        </>
    );
};
